import { useMemo, useState } from 'react';
import type { AgentRef, ComposeDraftSummary, Conversation, Inbox } from '../../../api/types';
import { ContactPanel } from '../ContactPanel';
import { useComms } from '../context';
import type { ConversationsState } from '../useConversations';
import { makeMailActions } from './actions';
import { GmailList } from './GmailList';
import { GmailThread } from './GmailThread';
import './gmail.css';

/**
 * The mail-client layout for the Email tab: a full-width list that becomes a
 * full-width reading view, rather than the CRM's list-beside-thread.
 *
 * It is a SHELL and nothing more. Every capability underneath — the same
 * `useConversations` rows, the same `useThread`, the same `Composer`, the same
 * booking draft, the same contact panel — is the three-pane layout's, reached
 * through the same hooks. A layout that re-implemented any of them would be a
 * second product to keep in step, and the first thing to rot would be the half
 * an agent only uses on Tuesdays.
 *
 * What it does own is the one thing the two layouts genuinely disagree about:
 * where the contact panel lives. Beside a 350px thread it is a third column;
 * over a full-width one it is a drawer behind the reading view's Profile
 * button — one of the three-pane header's controls, which sit beside the star
 * rather than in Gmail's own toolbar.
 */
export function GmailView({
  list,
  composeDrafts,
  onOpenComposeDraft,
  inboxById,
  agents,
  selected,
  onSelect,
  onSelectId,
  search,
  onSearch,
}: {
  list: ConversationsState;
  /** The Drafts view's other half — unsent NEW messages. See GmailList. */
  composeDrafts?: readonly ComposeDraftSummary[];
  onOpenComposeDraft?: (draft: ComposeDraftSummary) => void;
  inboxById: Map<number, Inbox>;
  agents: AgentRef[];
  /** The open conversation, or null for the list. Owned by the module's URL state. */
  selected: Conversation | null;
  onSelect: (conversation: Conversation) => void;
  onSelectId: (displayId: number | null) => void;
  search: string;
  onSearch: (next: string) => void;
}) {
  const { source } = useComms();
  const [error, setError] = useState<string | null>(null);
  const [ctxOpen, setCtxOpen] = useState(false);

  const actions = useMemo(
    () => makeMailActions(source.api, { onChanged: list.reload, onError: setError }),
    [source.api, list.reload],
  );

  return (
    <div className="gm">
      <div className="gm-main">
        {/* A refused action moves nothing on screen, so it has to be said. It
            sits above both halves because the toolbars are the same in each. */}
        {error && (
          <button
            type="button"
            className="gm-error"
            title="Dismiss"
            onClick={() => setError(null)}
          >
            {error}
          </button>
        )}
        {selected ? (
          <GmailThread
            key={selected.id}
            conversation={selected}
            inbox={inboxById.get(selected.inboxId)}
            actions={actions}
            onBack={() => onSelectId(null)}
            onChanged={list.reload}
            onSeen={list.markSeenLocal}
            contactOpen={ctxOpen}
            onContactOpen={setCtxOpen}
          />
        ) : (
          <GmailList
            rows={list.rows}
            composeDrafts={composeDrafts}
            onOpenComposeDraft={onOpenComposeDraft}
            inboxById={inboxById}
            agents={agents}
            loading={list.loading}
            stale={list.stale}
            refreshing={list.refreshing}
            error={list.error?.message ?? null}
            hasMore={list.hasMore}
            loadingMore={list.loadingMore}
            onLoadMore={list.loadMore}
            onOpen={onSelect}
            actions={actions}
            search={search}
            onSearch={onSearch}
          />
        )}
      </div>
      {/* The contact panel is the SAME component the three-pane layout puts in
          its third column — only its position differs, which is a stylesheet's
          business and not a second implementation's. */}
      {selected && ctxOpen && (
        <ContactPanel
          conversation={selected}
          onClose={() => setCtxOpen(false)}
          onOpenConversation={onSelectId}
        />
      )}
    </div>
  );
}
